import {TimelineCmdByEventAndIdAction, TimelineState, TimelineStateData} from './types';
import {
  MATCHDETAIL_CLEANUP,
  TIMELINE_CMD_BY_EVENT_AND_LAST_ID_GET_INVALIDATE,
  TIMELINE_CMD_BY_EVENT_AND_LAST_ID_GET_REQUEST,
  TIMELINE_CMD_BY_EVENT_AND_LAST_ID_GET_RESPONSE,
} from '../actionTypes';
import {TimelineUpdateCommand, TimelineUpdateCommandType} from '../../utils/types';
import {MatchDetailCleanup} from '../matchDetail/types';

const initialState: TimelineState = {
  isFetching: false,
  didInvalidate: false,
};

/**
 * This function alters the timeline data unrolling the updateCommands retrieved
 * by the API and applying them to the current state
 * @param cmds
 * @param sportEventId
 * @param stateData
 */
const reduceUpdateCommands = (
  cmds: TimelineUpdateCommand[],
  sportEventId: string,
  stateData?: TimelineStateData
): TimelineStateData => {
  const data =
    stateData && stateData.sportEventId === sportEventId
      ? stateData
      : {
          sportEventId,
          lastCmdId: undefined,
          updates: [],
        };

  if (cmds.length <= 0) return data;

  const {updates} = data;
  cmds.reverse().forEach(c => {
    switch (c.commandType) {
      /*
       * The ADD commands inserts the event at the correct index of the updates array
       * in order to keep the timeline in reverse chronological order.
       * The sorting field is the localDateTime of the BE datamodel, that is stored in update.time
       */
      case TimelineUpdateCommandType.ADD: {
        // do not add event if id is already present
        if (updates.findIndex(u => u.id === c.update.id) >= 0) break;

        const nextEventIdx = updates.findIndex(e => e.time <= c.update.time);
        if (nextEventIdx < 0) {
          updates.push(c.update);
        } else {
          updates.splice(nextEventIdx, 0, c.update);
        }
        break;
      }
      case TimelineUpdateCommandType.UPDATE: {
        const idx = updates.findIndex(e => e.id === c.update.id);
        if (idx > -1) updates[idx] = c.update;
        break;
      }
      case TimelineUpdateCommandType.DELETE: {
        const idx = updates.findIndex(e => e.id === c.update.id);
        if (idx > -1) updates.splice(idx, 1);
        break;
      }
      case TimelineUpdateCommandType.UNDEFINED:
        // eslint-disable-next-line no-console
        console.log(`skipped command ${c.id}, since it's type is UNDEFINED`);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log(`skipped command ${c.id}, since it's type ${c.commandType} is not recognized`);
    }
  });

  data.lastCmdId = cmds[cmds.length - 1].id;
  return data;
};

/**
 * Derives the 'timeline' state by reducing a new one every time an involved action is dispatches.
 * If the action is not involved (meaning it's not related with this state), then the state
 * is not changed.
 * @param state
 * @param action
 */
const timelineUpdateCmdsReducer = (
  state: TimelineState = initialState,
  action: TimelineCmdByEventAndIdAction | MatchDetailCleanup
): TimelineState => {
  switch (action.type) {
    case TIMELINE_CMD_BY_EVENT_AND_LAST_ID_GET_REQUEST:
      return {
        ...state,
        isFetching: true,
        error: undefined,
      };
    case TIMELINE_CMD_BY_EVENT_AND_LAST_ID_GET_RESPONSE:
      return {
        ...state,
        isFetching: false,
        didInvalidate: false,
        data: reduceUpdateCommands(action.payload.commands, action.payload.eventId, state.data),
      };
    case TIMELINE_CMD_BY_EVENT_AND_LAST_ID_GET_INVALIDATE:
      return {
        ...state,
        isFetching: false,
        didInvalidate: true,
        error: action.error,
      };
    case MATCHDETAIL_CLEANUP:
      return {...initialState};
    default:
      return state;
  }
};

export default timelineUpdateCmdsReducer;
